import { type NexusSignalDefinition, defineEvent, defineStore, otto } from '@otto-ec/global-resources/nexus';
import type {
    ResolveInput,
    SelectionAssignment,
    SelectionChange,
    SelectionOperationOptions,
    SelectionPage,
    SelectionResult,
    SelectionState,
} from '@otto-ec/nexus-registry/selection';
import { log } from '@shared/log.browser.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { LegacySelectionPublisher } from './LegacySelectionPublisher.browser.ts';
import { createProductionSelectionDependencies } from './SelectionDependencies.browser.ts';
import type {
    ResolvedSelectionResponse,
    SelectionDomAdapter,
    SelectionResolver,
    SelectionTrackingAdapter,
} from './SelectionDependencies.ts';

export type SelectionDependencies = {
    resolver: SelectionResolver;
    dom: SelectionDomAdapter;
    tracking: SelectionTrackingAdapter;
    eventsOutgoing?: EventsOutgoing;
};

const logger = log.scope('selection');

export class Selection {
    private latestOperation = 0;
    private nextOperationId = 0;
    private current: NexusSignalDefinition<SelectionState> | undefined;
    private readonly changes = defineEvent<SelectionChange>();
    private readonly eventsOutgoing: EventsOutgoing;
    private readonly legacyPublisher: LegacySelectionPublisher;

    public constructor(private readonly dependencies: SelectionDependencies = createProductionSelectionDependencies()) {
        this.eventsOutgoing = dependencies.eventsOutgoing ?? createEventsOutgoing();
        this.legacyPublisher = new LegacySelectionPublisher(dependencies.dom, this.eventsOutgoing);
    }

    public register(): () => void {
        if (this.current) throw new Error('Selection is already registered');
        logger.debug('Registering selection');

        try {
            const rule = this.dependencies.dom.bootstrapRule();
            const url = this.dependencies.dom.currentUrl();
            const state: SelectionState = {
                rule,
                page: pageFromUrl(url),
                url,
                entryRule: this.dependencies.dom.historyEntryRule() || rule,
            };
            this.current = defineStore<SelectionState>(state);
            return otto.selection.assignNamespace({
                resolve: this.resolve,
                assign: this.assign,
                current: this.current,
                changes: this.changes,
            });
        } catch (error) {
            logger.error('Failed to register selection', error);
            this.current = undefined;
            throw error;
        }
    }

    public readonly resolve = async (
        input: ResolveInput,
        options?: SelectionOperationOptions,
    ): Promise<SelectionResult> => {
        logger.debug('Resolving selection', input);
        const result = await this.resolveAttributes(input.attributes, options);
        await this.publishLegacyResolvedBestEffort(result);
        return result;
    };

    public readonly assign = async (
        input: SelectionAssignment,
        options?: SelectionOperationOptions,
    ): Promise<SelectionResult> => {
        validateRule(input.rule);
        if (!input.url) throw new Error('Invalid url');

        logger.debug('Assigning selection', input);

        const result = await this.perform(options, async (sequence) => {
            const response = await this.resolveSelection(new URLSearchParams({ rule: input.rule }), sequence);
            const entryRule = input.entryRule || otto.selection.current.get().entryRule;
            if (!entryRule) throw new Error('Selection assignment requires a non-empty entry rule');

            return {
                selection: {
                    rule: input.rule,
                    page: orderedPage(input.page),
                    url: input.url,
                    entryRule,
                },
                ...(response.count !== undefined ? { count: response.count } : {}),
            };
        });

        await this.publishLegacyResolvedBestEffort(result);
        return result;
    };

    public async resolveLegacy(attributes: Record<string, string | string[]>): Promise<void> {
        logger.debug('Resolving legacy selection', attributes);
        const result = await this.resolveAttributes(attributes);
        await this.legacyPublisher.publish(result);
    }

    private async resolveAttributes(
        attributes: Record<string, string | string[]>,
        options?: SelectionOperationOptions,
    ): Promise<SelectionResult> {
        const params = selectionParams(attributes);
        const page = pageFromAttributes(attributes);

        return this.perform(options, async (sequence) => {
            const response = await this.resolveSelection(params, sequence);
            const entryRule = otto.selection.current.get().entryRule;
            if (!entryRule) throw new Error('Failed to resolve rule: missing entry rule');

            return {
                selection: { rule: response.rule, page, url: response.url, entryRule },
                ...(response.count !== undefined ? { count: response.count } : {}),
            };
        });
    }

    private async perform(
        options: SelectionOperationOptions | undefined,
        operation: (sequence: number) => Promise<Omit<SelectionResult, 'operationId'>>,
    ): Promise<SelectionResult> {
        const sequence = ++this.latestOperation;
        const result = await operation(sequence);
        this.ensureLatest(sequence);
        const complete = { ...result, operationId: options?.operationId || `${Date.now()}-${++this.nextOperationId}` };

        if (complete.count !== 0) {
            otto.selection.current.emit(complete.selection, { retain: true });
            otto.selection.changes.emit({ selection: complete.selection, operationId: complete.operationId });
        }
        return complete;
    }

    private async resolveSelection(params: URLSearchParams, sequence: number): Promise<ResolvedSelectionResponse> {
        const response = await this.dependencies.resolver(params, sequence);
        this.ensureLatest(sequence);
        await this.applyResolvedResponse(response, sequence);
        return response;
    }

    private async applyResolvedResponse(response: ResolvedSelectionResponse, sequence: number): Promise<void> {
        this.ensureLatest(sequence);
        this.dependencies.dom.replaceHtml(response.html);
        try {
            await this.dependencies.tracking.replaceContext(response.url);
        } catch (error) {
            logger.error('Failed to replace selection tracking context', error);
        }
        /*                                                                                                 */
        this.ensureLatest(sequence);
    }

    private ensureLatest(sequence: number): void {
        if (sequence !== this.latestOperation)
            throw new Error('Selection operation was superseded by a newer operation');
    }

    private async publishLegacyResolvedBestEffort(result: SelectionResult): Promise<void> {
        try {
            await this.legacyPublisher.publish(result);
        } catch (error) {
            logger.error('Failed to publish legacy selection resolution', error);
        }
    }
}

function selectionParams(attributes: Record<string, string | string[]>): URLSearchParams {
    validateRule(attributes.rule);
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(attributes)) {
        if (typeof value === 'string') params.append(key, value);
        else if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
            value.forEach((entry) => params.append(key, entry));
        } else throw new Error(`Unsupported selection field "${key}"`);
    }
    return params;
}

function validateRule(rule: unknown): asserts rule is string {
    if (typeof rule !== 'string' || !rule || rule === 'undefined' || rule === 'null') throw new Error('Invalid rule');
}

function pageFromAttributes(attributes: Record<string, string | string[]>): SelectionPage {
    return orderedPage({
        layout: first(attributes.l),
        offset: first(attributes.o),
        sortOrder: first(attributes.sortiertnach),
        comparisonId: first(attributes.c),
    });
}

function pageFromUrl(url: string): SelectionPage {
    const params = new URL(url, window.location.origin).searchParams;
    return orderedPage({
        layout: params.get('l') || undefined,
        offset: params.get('o') || undefined,
        sortOrder: params.get('sortiertnach') || undefined,
        comparisonId: params.get('c') || undefined,
    });
}

function orderedPage(page: SelectionPage): SelectionPage {
    return {
        ...(page.layout !== undefined ? { layout: page.layout } : {}),
        ...(page.offset !== undefined ? { offset: page.offset } : {}),
        ...(page.sortOrder !== undefined ? { sortOrder: page.sortOrder } : {}),
        ...(page.comparisonId !== undefined ? { comparisonId: page.comparisonId } : {}),
    };
}

function first(value: string | string[] | undefined): string | undefined {
    return Array.isArray(value) ? value[0] : value;
}
